import React, { useEffect } from "react";
import { Form } from "antd";
import CommonDatePicker from "@/common/elements/DatePicker";
import TextField from "@/common/elements/InputElements/TextField";
import TimePicker from "@/common/elements/TimePicker";
import Select from "@/common/elements/InputElements/Select";
import Checkbox from "@/common/elements/InputElements/CheckBox";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import { useDispatch, useSelector } from "react-redux";
import { fetchTableNames } from "@/apis/config/tableNameApi";
import { getRefListNameByID } from "@/util/common_function";
import dayjs from "dayjs";
import { usePermissionMask } from "@/util/usePermissionMask";
import { usePermissionSync } from "@/util/usePermissionSync";
import PermissionGroup from "./PermissionGroup";
import {
  StyledGridCol,
  StyledGridRow,
  StyledSectionHeading,
} from "@/common/styles";

export default function RulesRecordLocksPermitsForm({
  form,
  initialValues = {},
  onFinish,
  disabled = false,
  isLoading = false,
}) {
  const dispatch = useDispatch();

  // fetch table names
  useEffect(() => {
    dispatch(fetchTableNames());
  }, [dispatch]);

  const tableNameOptions = useSelector((state) => state.tableNames.options);
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const {
    items: permissionItems,
    parsePermissionMask,
    buildPermissionMask,
  } = usePermissionMask();
  const onValuesChange = usePermissionSync(form, permissionItems);

  useEffect(() => {
    if (!initialValues) return;
    form.setFieldsValue({
      securityRulesRecordsID: initialValues.securityRulesRecordsID,
      ruleType: initialValues.ruleType || "RECORD",
      ruleActive: initialValues.ruleActive ?? true,
      startingPoint: initialValues.startingPoint,
      tableName: initialValues.tableName,
      recordID: initialValues.recordID,
      password: initialValues.password,
      // nested permission objects
      allowSetting: parsePermissionMask(initialValues.allowSetting),
      denySetting: parsePermissionMask(initialValues.denySetting),
      id: initialValues.securityRulesRecordsID,
      dateAdded: initialValues.dateAdded
        ? dayjs(initialValues.dateAdded)
        : dayjs(),
      timeAdded: initialValues.timeAdded
        ? dayjs(initialValues.timeAdded, "HH:mm")
        : dayjs(),
      user:
        initialValues.user ||
        getRefListNameByID("", userDetails?.staffId, true, false),
    });
  }, [initialValues, form, parsePermissionMask]);

  const handleFinish = (values) =>
    onFinish({
      ...values,
      // rebuild numeric mask
      allowSetting: buildPermissionMask(values.allowSetting),
      denySetting: buildPermissionMask(values.denySetting),
    });

  return (
    <Form
      form={form}
      onFinish={handleFinish}
      layout="vertical"
      disabled={disabled}
      onValuesChange={onValuesChange}
    >
      <StyledSectionHeading>
        <span>Rule Type and Status</span>
      </StyledSectionHeading>
      <StyledGridRow gutter={16}>
        <StyledGridCol lg={8}>
          <FormItem name="ruleType" label="Rule Type">
            <TextField disabled />
          </FormItem>
        </StyledGridCol>
        <StyledGridCol lg={8}>
          <FormItem
            name="ruleActive"
            label="Is Rule Active?"
            layout="horizontal"
            labelPostion="label-right"
            paddingTop="26px"
            valuePropName="checked"
          >
            <Checkbox />
          </FormItem>
        </StyledGridCol>
      </StyledGridRow>

      <StyledSectionHeading>
        <span>Record Lock Info</span>
      </StyledSectionHeading>
      <StyledGridRow gutter={16}>
        <StyledGridCol lg={24}>
          <FormItem name="startingPoint" label="Starting Point">
            <TextField />
          </FormItem>
        </StyledGridCol>
        <StyledGridCol lg={8}>
          <FormItem name="tableName" label="Table Name">
            <Select options={tableNameOptions} placeholder="Select Table" disabled={disabled}/>
          </FormItem>
        </StyledGridCol>
        <StyledGridCol lg={8}>
          <FormItem name="recordID" label="Record ID">
            <TextField />
          </FormItem>
        </StyledGridCol>
        <StyledGridCol lg={8}>
          <FormItem name="password" label="Password">
            <TextField />
          </FormItem>
        </StyledGridCol>
      </StyledGridRow>

      {/* Permissions */}
      <StyledGridRow gutter={16}>
        <PermissionGroup
          form={form}
          prefix="allowSetting"
          heading="Allowed Settings"
          permissionItems={permissionItems}
        />

        <PermissionGroup
          form={form}
          prefix="denySetting"
          heading="Denied Settings"
          permissionItems={permissionItems}
        />
      </StyledGridRow>

      <StyledSectionHeading marginTop="20px">
        <span>Security Rule (Record Lock) Administrative Information</span>
      </StyledSectionHeading>
      <StyledGridRow gutter={16}>
        <StyledGridCol lg={2}>
          <FormItem name="id" label="Id">
            <TextField disabled />
          </FormItem>
        </StyledGridCol>
        <StyledGridCol lg={6}>
          <FormItem name="dateAdded" label="Date Entered">
            <CommonDatePicker format="MM/DD/YYYY" disabled />
          </FormItem>
        </StyledGridCol>
        <StyledGridCol lg={6}>
          <FormItem name="timeAdded" label="Time Entered">
            <TimePicker format="HH:mm" disabled />
          </FormItem>
        </StyledGridCol>
        <StyledGridCol lg={6}>
          <FormItem name="user" label="User">
            <TextField disabled />
          </FormItem>
        </StyledGridCol>
      </StyledGridRow>
    </Form>
  );
}
